import { toastError, toastSuccess } from '@components/Toast/Toast';
import { useBankProfile } from '@contexts/BankProfileContext';
import { DEFAULT_PAGE_SIZE } from '@gurupay/gp-ui-library';
import type { BATCH_OPERATION_TYPE, OPERATION_TYPE } from '@pages/batch-operations/utils';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import type { TRANSACTION_STATUS, VOP_STATUS } from 'types/layout';
import type { OperationStatus } from 'types/operations';
import { BatchStatus } from 'types/operations';

import { useAuth } from '../../hooks';
import type { PaginatedItems } from './common';

export interface BatchFilters {
	status?: BatchStatus[];
	paymentType?: BATCH_OPERATION_TYPE[];
	dateTo?: string;
	dateFrom?: string;
	limit?: number;
	currentPage?: number;
}

export interface BatchItem {
	uuid: string;
	fileName: string;
	status: BatchStatus;
	uploadDate: string;
	operationsCount: number;
	errorCount: number;
	totalAmount: number;
	currency: string;
	executedAt: string;
	errorMessages: { error: TransactionErrorKey }[];
	failedToReadEntries: PaginatedItems<BatchErrorTransaction[]>;
	transactions: PaginatedItems<BatchTransactionProps[]>;
	resolvedAmount: number;
	resolvedOperationsCount: number;
}

interface BatchResponse {
	headers: any[];
	original: PaginatedItems<BatchItem[]>;
	exception: any;
}

export interface BatchErrorTransaction {
	uuid: string;
	batch_id: string;
	payment_details: string;
	beneficiary_account_number: string;
	beneficiary_name: string;
	amount: number;
	error_message: TransactionErrorKey;
}

export interface BatchTransactionProps {
	id: string;
	created_at: string;
	document_number: string;
	status: TRANSACTION_STATUS;
	payment_type: string;
	vopStatus: VOP_STATUS;
	beneficiary: {
		account_number: string;
		name: string;
	};
	amount: number;
	payment_details: string;
	errorMessages: {
		count: number;
		error: string;
	}[];
}
export interface FilterParams {
	type?: OPERATION_TYPE;
	status?: OperationStatus[];
	beneficiaryIban?: string;
	beneficiaryName?: string;
	description?: string;
	verification_of_payee_status?: VOP_STATUS;
	errorKeys?: string[];
}

type UseBatchTransactionsProps = {
	batchId: string;
	limit?: number;
	currentPage?: number;
	errorLimit?: number;
	errorCurrentPage?: number;
	filters: FilterParams;
};

export interface BatchSamplesProps {
	fileMimeType: string;
	originalName: string;
	type: string;
	url: string;
}

export enum TransactionErrorKey {
	INVALID_IBAN = 'batch_transaction.iban.invalid',
	COUNTRY_CODE_INVALID = 'batch_transaction.country_code.invalid',
	BANK_EMPTY_SWIFT = 'batch_transaction.bank.empty.swift',
	ADDRESS_EMPTY_SWIFT = 'batch_transaction.address.empty.swift',
	SAME_ACCOUNT = 'batch_transaction.same.account.failed',
	FAILED_TO_READ_ROW = 'batch_transaction.parse.failed',
}

export const BATCH_LIST_PAGE_SIZE = 10;

interface DeleteBatchPayload {
	id: string;
}

export const useDeleteBatch = (): UseMutationResult<void, Error, DeleteBatchPayload> => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();
	const queryClient = useQueryClient();
	const { t } = useTranslation('', { keyPrefix: 'payments.batch' });

	const profileId = selectedProfile?.id;

	return useMutation({
		mutationFn: async ({ id }: DeleteBatchPayload) => {
			await axios.delete(
				`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${profileId}/transactions/batch/${id}`,
				{
					headers: { Authorization: session?.tokenized },
				},
			);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['list-batches', profileId],
			});
			toastSuccess(t('batchDeleteSuccess'));
		},
		onError: () => {
			toastError(t('batchDeleteFailed'));
		},
	});
};

export const useBatchSamples = (): UseQueryResult<BatchSamplesProps[], Error> => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const profileId = selectedProfile?.id;

	const fetchBatchSamples = async (): Promise<BatchSamplesProps[]> => {
		if (!profileId) {
			throw new Error('Missing profileId');
		}

		const response = await axios.get<BatchSamplesProps[]>(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${profileId}/transactions/batch-templates`,
			{
				headers: {
					Authorization: session?.tokenized,
				},
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['batch-samples', profileId],
		queryFn: fetchBatchSamples,
		enabled: !!profileId && !!session,
		refetchOnWindowFocus: false,
	});
};

const useBatchUpload = () => {
	const { selectedProfile } = useBankProfile();
	const { session } = useAuth();

	const uploadBatch = async (file: File): Promise<{ batchId: string }[]> => {
		const formData = new FormData();
		formData.append('transactions', file);

		const response = await axios.post(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/transactions/batch`,
			formData,
			{
				headers: {
					Authorization: session?.tokenized,
					'Content-Type': 'multipart/form-data',
				},
			},
		);

		return response.data;
	};

	return useMutation({
		mutationFn: uploadBatch,
		onSuccess: (data) => data,
		onError: (data) => data,
	});
};

const getStatusForRequest = (status: BatchStatus[]) => {
	if (status?.includes(BatchStatus.EXECUTED)) {
		return [...status, BatchStatus.EXECUTED_WITH_WARNINGS];
	}

	if (status?.includes(BatchStatus.UNSIGNED)) {
		return [...status, BatchStatus.UNSIGNED_WITH_WARNINGS];
	}

	return status;
};

const useBatches = ({
	limit = BATCH_LIST_PAGE_SIZE,
	currentPage = 1,
	dateFrom,
	dateTo,
	status = [],
}: BatchFilters & { currentPage?: number; limit?: number }): UseQueryResult<
	PaginatedItems<BatchItem[]>,
	Error
> => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const resolvedParams = {
		page: currentPage,
		per_page: limit,
		...(dateFrom ? { 'filter[created_at][from]': dateFrom } : {}),
		...(dateTo ? { 'filter[created_at][to]': dateTo } : {}),
		...(status?.length ? { 'filter[statuses]': getStatusForRequest(status) } : {}),
	};

	const listBatches = async (): Promise<BatchResponse> => {
		const response = await axios.get(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/transactions/batch`,
			{
				headers: { Authorization: session?.tokenized },
				params: resolvedParams,
			},
		);

		return response.data;
	};

	return useQuery({
		queryKey: ['list-batches', limit, currentPage, dateFrom, dateTo, status],
		queryFn: listBatches,
		select: (data) => ({
			items: data.original.items.map((item) => ({
				...item,
				totalAmount: item.totalAmount / 100,
				resolvedAmount: item.resolvedAmount / 100,
			})),
			pagination: data.original.pagination,
		}),
	});
};

const useBatchTransactions = ({
	batchId,
	limit = DEFAULT_PAGE_SIZE,
	currentPage = 1,
	filters = {},
	errorLimit = DEFAULT_PAGE_SIZE,
	errorCurrentPage = 1,
}: UseBatchTransactionsProps): UseQueryResult<BatchItem, Error> => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	const params = {
		page: currentPage,
		per_page: limit,
		error_per_page: errorLimit,
		error_page: errorCurrentPage,
		...(filters.status?.length && { 'filter[statuses]': filters.status }),
		...(filters.type && { 'filter[payment_type]': filters.type }),
		...(filters.beneficiaryIban && {
			'filter[beneficiary_iban]': filters.beneficiaryIban,
		}),
		...(filters.beneficiaryName && {
			'filter[beneficiary_name]': filters.beneficiaryName,
		}),
		...(filters.description && { 'filter[payment_details]': filters.description }),
		...(filters.verification_of_payee_status && {
			'filter[verification_of_payee_status]': filters.verification_of_payee_status,
		}),
		...(filters.errorKeys?.length && { 'filter[error_messages][]': filters.errorKeys }),
	};

	const fetchBatchTransactions = async (): Promise<BatchItem> => {
		const response = await axios.get<BatchItem>(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile?.id}/transactions/batch/${batchId}`,
			{
				headers: { Authorization: session?.tokenized },
				params,
			},
		);

		const data = response.data;

		return {
			...data,
			totalAmount: data.totalAmount / 100,
			resolvedAmount: data.resolvedAmount / 100,
			failedToReadEntries: {
				...data.failedToReadEntries,
				items: data.failedToReadEntries.items.map((item) => ({
					...item,
					amount: item.amount / 100,
				})),
			},
			transactions: {
				...data.transactions,
				items: data.transactions.items.map((item) => ({
					...item,
					amount: item.amount / 100,
				})),
			},
		};
	};

	return useQuery({
		queryKey: [
			'batch-transactions',
			selectedProfile?.id,
			batchId,
			limit,
			currentPage,
			errorLimit,
			errorCurrentPage,
			filters,
		],
		queryFn: fetchBatchTransactions,
		enabled: !!selectedProfile?.id && !!batchId,
		placeholderData: keepPreviousData,
		staleTime: 30 * 1000,
		refetchInterval: (query) => {
			const status = query.state.data?.status;

			return status === 'INITIALIZING' ? 2000 : false;
		},
	});
};

export { useBatches, useBatchTransactions, useBatchUpload };
